import { Title, Group, Button, Stack, ActionIcon, Switch } from "@mantine/core";
import { DataTable } from "mantine-datatable";
import { IconPlus, IconPencil, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { foldersApi } from "../api/folders.js";
import type { ClientFolder } from "@cas/shared";

export function ClientFoldersList() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: folders, isLoading } = useQuery({ queryKey: ["folders"], queryFn: foldersApi.list });

  const toggle = useMutation({
    mutationFn: (f: ClientFolder) => foldersApi.upsert({ ...f, enabled: !f.enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["folders"] }),
  });
  const remove = useMutation({
    mutationFn: (domain: string) => foldersApi.remove(domain),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["folders"] }),
  });

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Client folders ({folders?.length ?? 0})</Title>
        <Button component={Link} to="/folders/new" leftSection={<IconPlus size={16} />}>Add client</Button>
      </Group>
      <DataTable
        striped withTableBorder highlightOnHover fetching={isLoading} idAccessor="domain"
        records={folders ?? []}
        columns={[
          { accessor: "enabled", title: "On", width: 60, render: (f) => <Switch checked={f.enabled} onChange={() => toggle.mutate(f)} /> },
          { accessor: "domain", title: "Domain", width: 250 },
          { accessor: "clientDisplayName", title: "Client" },
          { accessor: "folderId", title: "Folder ID", render: (f) => <code style={{ fontSize: 11 }}>{f.folderId.slice(0, 24)}…</code> },
          { accessor: "actions", title: "", width: 120, textAlign: "right",
            render: (f) => (
              <Group gap="xs" justify="flex-end">
                <ActionIcon variant="subtle" onClick={() => nav(`/folders/${encodeURIComponent(f.domain)}`)}><IconPencil size={16} /></ActionIcon>
                <ActionIcon variant="subtle" color="red" onClick={() => { if (confirm(`Delete client folder "${f.domain}"?`)) remove.mutate(f.domain); }}><IconTrash size={16} /></ActionIcon>
              </Group>
            ),
          },
        ]}
      />
    </Stack>
  );
}
